import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  App, Button, Drawer, Input, Space, Tooltip, Typography,
} from 'antd';
import { syncPipelineStages } from 'api/pipelines';
import React, { useEffect, useState } from 'react';
import { getHttpErrorMessage } from 'utils/http';

import { formatDealCount } from './utils';

const { Text } = Typography;

function isNewStage(stage) {
  return typeof stage.id === 'string' && stage.id.startsWith('new-');
}

function stageDeleteDisabledReason(stage, canDelete) {
  if ((stage.deal_count ?? 0) > 0) {
    return 'Move or delete deals on this stage before removing it.';
  }
  if (!canDelete) return 'The pipeline must have at least one stage.';
  return null;
}

function SortableStageRow({
  stage, onRename, onDelete, canDelete,
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: stage.id });

  const deleteDisabledReason = stageDeleteDisabledReason(stage, canDelete);
  const deleteDisabled = Boolean(deleteDisabledReason);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <HolderOutlined style={{ cursor: 'grab', touchAction: 'none' }} {...attributes} {...listeners} />
      <Input
        value={stage.name}
        onChange={(e) => onRename(stage.id, e.target.value)}
        style={{ flex: 1 }}
      />
      <Text type="secondary">
        {formatDealCount(stage.deal_count ?? 0)}
      </Text>
      <Tooltip title={deleteDisabled ? deleteDisabledReason : 'Remove stage when you save'}>
        <span style={{ display: 'inline-flex' }}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={deleteDisabled}
            onClick={() => onDelete(stage.id)}
          />
        </span>
      </Tooltip>
    </div>
  );
}

function EditStagesDrawer({
  open, stages: initialStages, onClose, onSaved,
}) {
  const { message } = App.useApp();
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    if (open) {
      setStages(initialStages.map((s) => ({ ...s })));
    }
  }, [open, initialStages]);

  const handleRename = (id, name) => {
    setStages((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setStages((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleAddStage = () => {
    const n = stages.filter((s) => isNewStage(s)).length + 1;
    setStages((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        name: n > 1 ? `New stage ${n}` : 'New stage',
        deal_count: 0,
      },
    ]);
  };

  const handleDelete = (stageId) => {
    setStages((prev) => prev.filter((s) => s.id !== stageId));
  };

  const handleSave = async () => {
    if (stages.length < 1) {
      message.error('The pipeline must have at least one stage.');
      return;
    }
    setSaving(true);
    try {
      await syncPipelineStages(
        stages.map((stage) => ({
          id: isNewStage(stage) ? null : stage.id,
          name: (stage.name || '').trim() || 'New stage',
        })),
      );
      message.success('Pipeline stages updated');
      onSaved();
      onClose();
    } catch (err) {
      message.error(getHttpErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const canDelete = stages.length > 1;

  return (
    <Drawer
      title="Edit pipeline stages"
      open={open}
      onClose={onClose}
      width={480}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </Space>
        </div>
      )}
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {stages.map((stage) => (
            <SortableStageRow
              key={stage.id}
              stage={stage}
              onRename={handleRename}
              onDelete={handleDelete}
              canDelete={canDelete}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button type="dashed" block icon={<PlusOutlined />} onClick={handleAddStage} style={{ marginTop: 16 }}>
        Add stage
      </Button>
    </Drawer>
  );
}

export default EditStagesDrawer;
